/* eslint-disable react/prop-types */
import { DatePicker, Dropdown, Input, Textarea } from 'ccs-ui-components';
import moment from 'moment';
import { ChangeEvent, useEffect, useState } from 'react';
import { Col, Form, FormGroup, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { serviceTypes } from '../../../../constants';
import { IFormFieldData } from '../../../../models';
import { AppDispatch, useSelector } from '../../../../store/AppStore';
import { getScheduledMaintenanceRequest } from '../../../../store/actions/sagaActions/schedule-maintenance/GetScheduledMaintenanceSagaActions';
import classes from '../scheduled-maintenance-details-wrapper/ScheduledMaintenanceDetailsWrapper.module.scss';
import { SCHEDULE_MAINTENANCE_MODE, getModeFromUrl } from '../scheduled-maintenance-details-wrapper/utilities/AddScheduleMaintenanceUtils';
import {
  filterFromTimeHandler,
  filterTimeHandler
} from '../scheduled-maintenance-details-wrapper/utilities/helperDateFunction';

const format = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';

interface IScheduledMaintenanceDetailsForm {
  handleFormData: (data: IFormFieldData) => void;
}
enum maintenanceNameValidation {
  VALID = 'VALID',
  INVALID = 'INVALID',
  EMPTY = 'EMPTY',
  EXIST = 'EXIST',
  LENGTH_EXCEEDED = 'LENGTH_EXCEEDED',
  NETWORK_FAILURE = 'NETWORK_FAILURE'
}

enum maintenanceEmailValidation {
  VALID = 'VALID',
  INVALID = 'INVALID',
  EMPTY = 'EMPTY'
}

export const ScheduledMaintenanceDetailsForm: React.FC<
  IScheduledMaintenanceDetailsForm
> = ({ handleFormData }) => {
  /* Local states */
  const { t: translate } = useTranslation();
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [maintenanceName, setMaintenanceName] = useState('');
  const [maintenanceNameComp, setMaintenanceNameComp] = useState('');
  const [maintenanceDesc, setMaintenanceDesc] = useState('');
  const [emptyMaintenanceDesc, setEmptyMaintenanceDesc] = useState(false);
  const [email, setEmail] = useState('');
  const [emptyFromDate, setEmptyFromDate] = useState(false);
  const [emptyToDate, setEmptyToDate] = useState(false);
  const [isView, setIsView] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [disableMaintenance, setDisableMaintenance] = useState(false);
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [invalidMaintenanceName, setInvalidMaintenanceName] = useState(maintenanceNameValidation.VALID);
  const [invalidMaintenanceEmail, setInvalidMaintenanceEmail] = useState(maintenanceEmailValidation.VALID);
  // Hooks
  const dispatch = useDispatch<AppDispatch>();
  /* Get Data from the store */
  const { SmLogHistoryDetails: data } = useSelector((state) => state.LogHistory);
  const { ScheduledMaintenanceDetails, isLoading, errorMessage, success } = useSelector((state: any) => state?.ScheduledMaintenanceReducer);
  /* Local Variables */
  const maintenanceModeFromUrl = getModeFromUrl();
  const maintenanceNameArray = ScheduledMaintenanceDetails.serviceScheduleMaintenance?.map((data: any) => data.maintenanceName);
  const maintenanceNameMaxLength = 44;

  const errorTextConstants = {
    maintenance_name_required: translate('add_sm_form.maintenance_name_required'),
    maintenance_name_invalid: translate('add_sm_form.maintenance_name_invalid'),
    maintenance_name_exist: translate('add_sm_form.maintenance_name_exist'),
    maintenance_length_exceeded: translate('add_sm_form.maintenance_length_exceeded'),
    maintenance_name_network_failure: translate('add_sm_form.maintenance_name_network_failure'),
    maintenance_email_required: translate('add_sm_form.maintenance_email_required'),
    maintenance_email_invalid: translate('add_sm_form.maintenance_email_invalid'),
  };

  const errorText = (fieldName: any) => {
    if ((fieldName === 'name' && invalidMaintenanceName) === maintenanceNameValidation.EMPTY) {
      return errorTextConstants.maintenance_name_required;
    }
    if ((fieldName === 'name' && invalidMaintenanceName) === maintenanceNameValidation.INVALID) {
      return errorTextConstants.maintenance_name_invalid;
    }
    if ((fieldName === 'name' && invalidMaintenanceName) === maintenanceNameValidation.EXIST) {
      return errorTextConstants.maintenance_name_exist;
    }
    if ((fieldName === 'name' && invalidMaintenanceName) === maintenanceNameValidation.LENGTH_EXCEEDED) {
      return errorTextConstants.maintenance_length_exceeded;
    }
    if ((fieldName === 'name' && invalidMaintenanceName) === maintenanceNameValidation.NETWORK_FAILURE) {
      return errorTextConstants.maintenance_name_network_failure;
    }
    if ((fieldName === 'email' && invalidMaintenanceEmail) === maintenanceEmailValidation.EMPTY) {
      return errorTextConstants.maintenance_email_required;
    }
    if ((fieldName === 'email' && invalidMaintenanceEmail) === maintenanceEmailValidation.INVALID) {
      return errorTextConstants.maintenance_email_invalid;
    }
    return '';
  };

  useEffect(() => {
    handleFormData({ name, value, invalidMaintenanceName, invalidMaintenanceEmail });
  }, [invalidMaintenanceEmail, invalidMaintenanceName]);

  useEffect(() => {
    if (success) {
      if (maintenanceNameComp && maintenanceNameArray && maintenanceNameArray.length > 0 && maintenanceNameArray.includes(maintenanceNameComp)) {
        setIsValid(false);
      } else setIsValid(true);
    }
  }, [success, maintenanceNameArray]);

  useEffect(() => {
    if (!isValid) setInvalidMaintenanceName(maintenanceNameValidation.EXIST);
    else setInvalidMaintenanceName(maintenanceNameValidation.VALID);
  }, [isValid]);

  useEffect(() => {
    if (maintenanceNameComp && invalidMaintenanceName === maintenanceNameValidation.VALID) payloadDispatch();
  }, [maintenanceNameComp]);

  useEffect(() => {
    if (errorMessage && maintenanceName) setInvalidMaintenanceName(maintenanceNameValidation.NETWORK_FAILURE);
  }, [errorMessage]);

  const payloadDispatch = () => {
    const payload: any = {
      maintenanceName: maintenanceNameComp,
      maintenanceStatus: '',
      pageNumber: 0,
      pageSize: 10,
      columnId: 'startTimestamp',
      sortType: 'DESC'
    };
    dispatch(getScheduledMaintenanceRequest(payload));
  };

  useEffect(() => {
    if (data[0]?.maintenanceName) {
      setDisableMaintenance(true);
      setMaintenanceName(data[0]?.maintenanceName);
      setMaintenanceDesc(data[0]?.message);
      setEmail(data[0]?.usersEmail?.join(','));
      setFromDate(new Date(data[0]?.startTimestamp));
      setToDate(new Date(data[0]?.endTimestamp));
    }
    if (maintenanceModeFromUrl.toUpperCase() === SCHEDULE_MAINTENANCE_MODE.VIEW.toUpperCase()) {
      setSelectedServiceType(data[0]?.serviceType);
      setIsView(true);
    } else if (maintenanceModeFromUrl.toUpperCase() === SCHEDULE_MAINTENANCE_MODE.EDIT.toUpperCase()) {
      setIsView(false);
      setSelectedServiceType(data[0]?.serviceType);
    }
  }, [data]);

  const validateEmails = (text: string) => {
    const emailAddresses = text.split(',').map((email) => email.trim());

    if (
      emailAddresses.every((email) =>
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email))
    ) {
      setInvalidMaintenanceEmail(maintenanceEmailValidation.VALID);
    } else {
      setInvalidMaintenanceEmail(maintenanceEmailValidation.INVALID);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setName(name);
    setValue(value);
    const regex = /^[a-zA-Z]([a-zA-Z0-9-])*$/;
    switch (name) {
      case 'name':
        setMaintenanceName(value);
        if (!value) {
          setInvalidMaintenanceName(maintenanceNameValidation.EMPTY);
        } else if (value.length > maintenanceNameMaxLength) {
          setInvalidMaintenanceName(maintenanceNameValidation.LENGTH_EXCEEDED);
        } else if (regex.test(value)) {
          setInvalidMaintenanceName(maintenanceNameValidation.VALID);
        } else {
          setInvalidMaintenanceName(maintenanceNameValidation.INVALID);
        }
        handleFormData({ name, value, invalidMaintenanceName, invalidMaintenanceEmail });
        break;
      case 'message':
        setMaintenanceDesc(value);
        setEmptyMaintenanceDesc(!value);
        handleFormData({ name, value, invalidMaintenanceName, invalidMaintenanceEmail });
        break;
      case 'email':
        setEmail(value);
        if (!value) {
          setInvalidMaintenanceEmail(maintenanceEmailValidation.EMPTY);
        } else if (value) validateEmails(value);
        handleFormData({ name, value, invalidMaintenanceName, invalidMaintenanceEmail });
        break;
      default:
        break;
    }
  };

  const handleInputBlur = (e: any) => {
    const { name, value } = e.target;
    setName(name);
    setValue(value);
    switch (name) {
      case 'name':
        if (invalidMaintenanceName !== (maintenanceNameValidation.EMPTY || maintenanceNameValidation.INVALID || maintenanceNameValidation.LENGTH_EXCEEDED)) {
          setMaintenanceNameComp(value);
        }
        if (!value) {
          setInvalidMaintenanceName(maintenanceNameValidation.EMPTY);
          break;
        }
        if (!isValid) {
          setInvalidMaintenanceName(maintenanceNameValidation.EXIST);
        }
        break;
      case 'message':
        setMaintenanceDesc(value);
        setEmptyMaintenanceDesc(!value);
        handleFormData({ name, value, invalidMaintenanceName, invalidMaintenanceEmail });
        break;
      case 'email':
        if (!value) setInvalidMaintenanceEmail(maintenanceEmailValidation.EMPTY);
        handleFormData({ name, value, invalidMaintenanceName, invalidMaintenanceEmail });
        break;
      default:
        break;
    }
  };

  const handleDateChange = (name: string, dateChanged: Date | null) => {
    let value = '';
    if (name === 'fromDate') {
      setEmptyFromDate(!dateChanged);
      value = dateChanged ? moment(dateChanged).utc().format(format) : '';
    } else {
      setEmptyToDate(!dateChanged);
      value = dateChanged ? moment(dateChanged).utc().format(format) : '';
    }
    handleFormData({ name, value, invalidMaintenanceEmail, invalidMaintenanceName });
  };

  const handleChangeServiceType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedServiceType(e.target.value);
    handleFormData({ name: e.target.name, value: e.target.value, invalidMaintenanceEmail, invalidMaintenanceName });
  };

  return (
    <Row className={classes['add-ScheduledMaintenanc-form-row']}>
      <Col md={4} className="no-padding">
        <div
          className={
            classes['add-ScheduledMaintenanc-form-container-col-title']
          }
          data-testid="formHeaderText"
        >
          {translate('add_sm_form.scheduled_maintenance')}
        </div>
        <div
          className={
            classes['add-ScheduledMaintenanc-form-container-col-sub-title']
          }
          data-testid="smdesc"
        >
          {translate('add_sm_form.scheduled_maintenance_desc')}
        </div>
      </Col>
      <Col md={7} className="no-padding">
        <Form>
          <FormGroup
            className={classes['add-ScheduledMaintenanc-form-group']}
          >
            <Input
              labelText={String(translate('add_sm_form.txt_name'))}
              isMandatory
              helperText={String(translate('add_sm_form.txt_name_helper'))}
              label=""
              value={maintenanceName}
              name="name"
              placeholder={String(translate('add_sm_form.txt_name_placeholder'))}
              data-testid="mname"
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              disabled={isView || disableMaintenance || isLoading}
              error={(invalidMaintenanceName !== maintenanceNameValidation.VALID)}
              errorTxt={errorText('name')}
              isLoading={isLoading}
              maxLength={Number('45')}
            />
          </FormGroup>
          <FormGroup
            className={classes['add-ScheduledMaintenanc-form-group']}
          >
            <Textarea
              labelText={String(translate('add_sm_form.txt_message'))}
              isMandatory
              helperText={String(translate('add_sm_form.txt_message_helper'))}
              name="message"
              label=""
              value={maintenanceDesc}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              disabled={isView}
              data-testid="msg-text"
              placeholder={String(translate('add_sm_form.txt_message_placeholder'))}
              className={emptyMaintenanceDesc ? classes['error-border'] : ''}
            />
            {emptyMaintenanceDesc && (
              <Form.Label className={classes['error-label']}>
                {translate('add_sm_form.txt_message_warning')}
              </Form.Label>
            )}
          </FormGroup>
          <div className={classes['add-ScheduledMaintenanc-form-date-range']}>
            <FormGroup
              className={classes['add-ScheduledMaintenanc-form-comp-type']}
            >
              <div>
                <Dropdown
                  labelText={String(translate('add_sm_form.txt_dropdown_service_type'))}
                  isMandatory
                  label=""
                  data={serviceTypes}
                  disabled={isView}
                  name="servicetype"
                  className={classes['dropdown-control']}
                  value={selectedServiceType}
                  onChange={handleChangeServiceType}
                  data-testid="category-dropdown"
                  placeholder={String(translate('add_sm_form.txt_dropdown_placeholder'))}
                />
              </div>
            </FormGroup>
            <div
              className={classes['add-ScheduledMaintenanc-form-date-fromdate']}
            >
              <DatePicker
                labelText={String(translate('add_sm_form.txt_start_date_label'))}
                isMandatory
                selected={fromDate}
                onChange={(date: Date | null) => {
                  setFromDate(date);
                  handleDateChange('fromDate', date);
                }}
                minDate={new Date()}
                maxDate={toDate ?? undefined}
                placeholderText={String(translate('add_sm_form.txt_start_date_placeholder'))}
                showTimeSelect
                timeFormat="HH:mm:ss"
                dateFormat="yyyy-MM-dd HH:mm:ss"
                timeIntervals={15}
                className={classes['react-datepicker']}
                disabled={isView}
                data-testid="fromdate"
                filterTime={(time: Date) => filterFromTimeHandler(time, toDate ?? new Date())}
              />
              {emptyFromDate && (
                <Form.Label className={classes['error-label']}>
                  {translate('add_sm_form.txt_start_date_error')}
                </Form.Label>
              )}
            </div>
            <div
              className={classes['add-ScheduledMaintenanc-form-date-todate']}
            >
              <DatePicker
                labelText={String(translate('add_sm_form.txt_end_date_label'))}
                isMandatory
                selected={toDate}
                onChange={(date: Date | null) => {
                  setToDate(date);
                  handleDateChange('toDate', date);
                }}
                minDate={fromDate ?? undefined}
                placeholderText={String(translate('add_sm_form.txt_start_date_placeholder'))}
                showTimeSelect
                timeFormat="HH:mm:ss"
                dateFormat="yyyy-MM-dd HH:mm:ss"
                timeIntervals={15}
                disabled={isView || fromDate === null}
                filterTime={(time: Date) => filterTimeHandler(time, fromDate ?? new Date())}
              />
              {emptyToDate && (
                <Form.Label className={classes['error-label']}>
                  {translate('add_sm_form.txt_end_date_error')}
                </Form.Label>
              )}
            </div>
          </div>
          <div
            className={classes['add-ScheduledMaintenanc-form-addtinal-data']}
          >
            <FormGroup
              className={
                classes['add-ScheduledMaintenanc-form-group-notifyuser']
              }
            >
              <Input
                labelText={String(translate('add_sm_form.txt_notify_user'))}
                isMandatory
                helperText={String(translate('add_sm_form.txt_email_helper'))}
                label=""
                value={email}
                name="email"
                placeholder={String(translate('add_sm_form.txt_email_placeholder'))}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isView}
                data-testid="emailId"
                error={
                  invalidMaintenanceEmail !== maintenanceEmailValidation.VALID
                }
                errorTxt={errorText('email')}
              />
            </FormGroup>
          </div>
        </Form>
      </Col>
    </Row>
  );
};
